import styles from './Row.module.scss';

import { Check } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { flexRender, Row } from '@tanstack/react-table';
import clsx from 'clsx';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CardConfig } from '@work4all/models/lib/table-schema/card-config';

import { EditModeCell } from '../../../../plugins/useEditMode';
import { ICssClasses } from '../../../../types';
import { getCommonPinningStyles } from '../../../get-common-pinning-styles';
import { TableCell } from '../../../table-cell/TableCell';
import { TableRow as TableRowComponent } from '../../../table-row/TableRow';
import { GroupedV8 } from '../grouped/GroupedV8';

import { reportMissingColumn } from './report-missing-column';

export const TABLE_ROW_DRAG_DROP_KEY = 'TABLE_ROW_DRAG_DROP';
const COLUMNS_WITHOUT_TOOLTIP_AND_ELLIPSIS = [
  'favoriteFlag',
  'trafficLightStatus',
];

interface TableRowV8Props {
  cardsView: boolean;
  cardConfig: CardConfig | null;
  row: Row<unknown>;
  className?: string;
  style?: React.CSSProperties;
  classes?: ICssClasses;
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  isGroupFooter?: boolean;
  isUnderPressSelect?: boolean;
  setUnderPressSelect?: (value: boolean) => void;
}

function TableRowV8(props: TableRowV8Props) {
  const {
    row,
    classes,
    cardsView = false,
    cardConfig: card,
    isGroupFooter,
    isUnderPressSelect,
    setUnderPressSelect,
  } = props;
  const cellClassName = classes?.cell;
  const { t } = useTranslation();

  const allCells = row.getAllCells();
  const visibleCells = row.getVisibleCells();

  const cellsById = useMemo(() => {
    return new Map(allCells.map((cell) => [cell.column.id, cell]));
  }, [allCells]);

  const availableInfoRows = useMemo(() => {
    return (
      card?.info?.filter((info) => {
        const cell = visibleCells.find((cell) => cell.column.id === info.key);
        return !!cell && info.columns.some((id) => row.getValue(id));
      }) ?? []
    );
  }, [card?.info, row, visibleCells]);

  if (row.getIsGrouped() || (row.original as any)?.isGrouped) {
    return <GroupedV8 cellClassName={cellClassName} row={row} />;
  }

  function cell(id?: string, context?: object): React.ReactNode {
    if (id === undefined) {
      return null;
    }

    const cell = cellsById.get(id);

    if (!cell) {
      reportMissingColumn(id);
      return null;
    }

    return flexRender(cell.column.columnDef.cell, {
      ...cell.getContext(),
      ...context,
    });
  }

  function cells(ids?: string[], inline?: boolean): React.ReactNode {
    if (ids === undefined) {
      return null;
    }

    ids.forEach((id) => {
      if (!cellsById.has(id)) {
        reportMissingColumn(id);
      }
    });

    const cells = ids
      .filter((id) => Boolean(cellsById.get(id) && row.getValue(id)))
      .map((id) => cell(id))
      .filter(Boolean);

    if (inline) {
      return cells.flatMap((content, index) => {
        if (index === 0) return [<Fragment key={index}>{content}</Fragment>];
        return [
          <Fragment key={`separator-${index}`}> | </Fragment>,
          <Fragment key={`content-${index}`}>{content}</Fragment>,
        ];
      });
    }

    return cells.flatMap((content, index) => {
      const node = (
        <Box
          key={index}
          component="div"
          style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
          sx={{ '&:empty::before': { content: '"-"' } }}
        >
          {content}
        </Box>
      );

      if (index === 0) {
        return [node];
      }

      return [<div key={`separator-${index}`}> | </div>, node];
    });
  }

  if (cardsView && card !== null) {
    const content = (
      <TableRowComponent
        onClick={props.onClick}
        className={props.classes?.row}
        isSelected={row.getIsSelected()}
        displayModifiers={row.displayModifiers}
        cardsView={true}
      >
        <TableCell
          cardsView={cardsView}
          component="button"
          columnId="none"
          style={{ gridArea: 'icon', height: 'auto' }}
          onClick={(e) => {
            e.preventDefault();
            if (!isUnderPressSelect && setUnderPressSelect) {
              setUnderPressSelect(true);
            }
            row.toggleSelected();
          }}
        >
          {isUnderPressSelect && row.getIsSelected() ? (
            <div className={styles.selectedIcon}>
              <Check />
            </div>
          ) : (
            <div
              style={{
                filter: isUnderPressSelect ? 'grayscale(100%)' : 'none',
              }}
            >
              {cell(card.icon, { placeholder: true })}
            </div>
          )}
        </TableCell>

        <TableCell
          cardsView={cardsView}
          columnId="none"
          className="title-cell"
          style={{ gridArea: 'title', minWidth: 0, display: 'flex' }}
        >
          {Array.isArray(card.title)
            ? cells(card.title, true)
            : cell(card.title)}
        </TableCell>

        {card.content && (
          <TableCell
            cardsView={true}
            columnId="none"
            style={{ gridArea: 'content', minWidth: 0, paddingTop: 0 }}
          >
            <Typography
              variant="caption"
              sx={{
                overflowWrap: 'anywhere',
                whiteSpace: 'normal',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                color: 'unset',
              }}
            >
              {cell(card.content)}
            </Typography>
          </TableCell>
        )}

        <TableCell
          cardsView={true}
          columnId="none"
          style={{
            gridArea: 'info',
            height: 'auto',
            minWidth: 0,
            paddingTop: 0,
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gap: '0 0.5rem',
            }}
          >
            {availableInfoRows.map((info) => {
              return (
                <Fragment key={info.key}>
                  <Typography
                    key={`${info.key}_title`}
                    variant="caption"
                    component="div"
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'flex',
                      gap: '.25rem',
                      '&:empty::before': { content: '"-"' },
                    }}
                  >
                    {t((info.title as string).trim())}:
                  </Typography>

                  <Typography
                    key={`${info.key}_value`}
                    variant="caption"
                    component="div"
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'flex',
                      gap: '.25rem',
                      '&:empty::before': { content: '"-"' },
                    }}
                  >
                    {cells(info.columns)}
                  </Typography>
                </Fragment>
              );
            })}
          </div>
        </TableCell>

        {card.secondaryIcons.length > 0 && (
          <TableCell
            cardsView={true}
            columnId="none"
            style={{ gridArea: 'secondary-icons', height: 'auto' }}
          >
            <div className={styles.secondaryIcons}>
              {card.secondaryIcons.slice(0, 3).map((columnId) => {
                return <Fragment key={columnId}>{cell(columnId)}</Fragment>;
              })}
            </div>
          </TableCell>
        )}
      </TableRowComponent>
    );

    if (card.wrapper)
      return <card.wrapper item={row.original}>{content}</card.wrapper>;
    return content;
  }

  return (
    <TableRowComponent
      onClick={props.onClick}
      className={clsx(props.classes?.row, styles.row)}
      style={props.style}
      isSelected={row.getIsSelected()}
      displayModifiers={row.displayModifiers}
      groupLevel={row.depth}
    >
      {row.getVisibleCells().map((cell) => {
        const { dataType, alignment } = cell.column.columnDef;

        const commonCellProps = {
          dataType,
          columnId: cell.column.id,
          alignment,
          style: getCommonPinningStyles(cell.column),
        };

        const commonCellPropsWithCellClassName = {
          ...commonCellProps,
          className: cellClassName,
        };

        if (cell.getIsAggregated()) {
          <TableCell key={cell.id} {...commonCellPropsWithCellClassName}>
            {flexRender(
              cell.column.columnDef.aggregatedCell,
              cell.getContext()
            )}
          </TableCell>;
        }

        if (cell.getIsPlaceholder()) {
          return (
            <TableCell {...commonCellPropsWithCellClassName} key={cell.id} />
          );
        }

        if (COLUMNS_WITHOUT_TOOLTIP_AND_ELLIPSIS.includes(cell.column.id)) {
          return (
            <TableCell
              key={cell.id}
              {...commonCellPropsWithCellClassName}
              disableTooltip
              disableEllipsis
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          );
        }

        if ((cell as unknown as EditModeCell).isEditMode) {
          return (
            <TableCell {...commonCellPropsWithCellClassName} key={cell.id}>
              {/* TODO Check if it's needed for anything */}
              {/* It might be used in Attachments https://work4all.atlassian.net/browse/WW-6307 */}

              {/* {cell.render('EditableCell', {
                isEditable: true,
                isEditMode: true,
                stopPropagation: true,
              })} */}
            </TableCell>
          );
        }

        if (isGroupFooter) {
          return (
            <TableCell key={cell.id} {...commonCellProps}>
              {cell.column.columnDef.footer != null
                ? flexRender(cell.column.columnDef.cell, cell.getContext())
                : null}
            </TableCell>
          );
        }

        return (
          <TableCell key={cell.id} {...commonCellPropsWithCellClassName}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        );
      })}
    </TableRowComponent>
  );
}

export { TableRowV8 as RowV8 };
